import { useCallback, useEffect, useMemo, useState } from 'react';

import type { NotificationsType } from '~/components/ClaimNotificationsCard/types';
import type { NotificationTypeCount } from '~/components/Notifications/types';
import {
  mapPossibleNotificationsTypesToIds,
  type PossibleNotificationTypeByIdMap,
} from '~/components/Notifications/utils';
import { usePrevious } from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';

interface UseLazyNotificationTypeCountReturnType {
  loadIfNeeded: () => Promise<void>;
  reloadData: () => Promise<NotificationTypeCount[] | undefined>;
  isLoading: boolean;
  isError: boolean;
  typesCount: NotificationTypeCount[] | undefined;
  notificationTypeToTypeConfig: PossibleNotificationTypeByIdMap;
  sortedPossibleNotificationTypes: NotificationsType[];
}

/**
 * Hook for lazy loading notification type counts.
 * Only fetches counts when explicitly requested via loadIfNeeded().
 *
 * @param claimId - Optional claim ID to filter counts by claim
 * @returns Object containing loading state, data, and control functions
 */
export const useLazyNotificationTypeCount = (
  organizationId: number,
  claimId?: number
): UseLazyNotificationTypeCountReturnType => {
  const [isLoading, setIsLoading] = useState(false);
  const prevClaimId = usePrevious(claimId);
  const {
    data: typesCount,
    isError,
    reloadData,
  } = useDataFetcher<NotificationTypeCount[]>(
    `/api/v1/notifications/${organizationId}/notification_type_counts`,
    {
      params: {
        claim_id: claimId,
      },
    },
    { shouldFetch: false }
  );

  useEffect(() => {
    // This is far from perfect, but it's an unlikely (currently impossible) scenario,
    // so it'll do for now, to avoid unnecessary complexity
    const isStale = claimId !== prevClaimId && !!typesCount;
    if (isStale) {
      reloadData();
    }
  }, [claimId, prevClaimId, typesCount, reloadData]);

  const loadIfNeeded = useCallback(async () => {
    // not loading in case an error occurred, to avoid infinite loading retries
    if (!typesCount && !isLoading && !isError) {
      setIsLoading(true);
      try {
        await reloadData();
      } finally {
        setIsLoading(false);
      }
    }
  }, [reloadData, typesCount, isLoading, isError]);

  const { map: notificationTypeToTypeConfig, sortedIds: sortedPossibleNotificationTypes } = useMemo(() => {
    if (!typesCount) {
      return { map: {}, sortedIds: [] };
    }
    return mapPossibleNotificationsTypesToIds(typesCount);
  }, [typesCount]);

  return {
    isLoading,
    isError,
    loadIfNeeded,
    reloadData,
    typesCount,
    notificationTypeToTypeConfig,
    sortedPossibleNotificationTypes,
  };
};
